import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";  // Use useLocation to check the current path
import { Package2, PanelLeft } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "../../component/ui/tooltip";
import { Button } from "../../component/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../component/ui/dropdown-menu";
import { HiOutlineBars3 } from "react-icons/hi2";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  useBreadCrumb,
} from "../../component/ui/breadcrumb";
import {
  dashboardIcon,
  dashboardIconBlue,
  emailIcon,
  emailIconBlue,
  investorIcon,
  investorIconBlue,
  managmentIcon,
  reportIcon,
  reportIconBlue,
  uploadIcon,
  uploadIconBlue,
} from "../../assets/icons";
import Investors from "./Investors";
import Schemes from "./Schemes";
import Ticker from "./Ticker";
import EmailTemplate from "./EmailTemplates";
import { UserManagement } from "./UserManagement";
import { UploadData } from "./UploadData";
import InvestorUploadReports from "../../component/investor/InvestorUploadReports";  // Assuming this is the component
import { useDispatch, useSelector } from "react-redux";
import { loaderSet } from "../../context/actions/loaderAction";
import { nullUserDetails } from "../../context/actions/userAction";
import Profile from "../../component/profile";
export function Layout() {

  const [collapsed, setCollapsed] = useState(true); // Sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false); // Mobile sidebar state
  const { selectedTab, setSelectedTab } = useBreadCrumb();
  const navigate = useNavigate();
  const location = useLocation();  // Get the current location/path
  const dispatch = useDispatch()
  const userInfo = useSelector(state => state.user)
  const handleToggle = () => {
    setCollapsed(!collapsed); // Toggle collapsed state
  };

  const handleMobileSidebarToggle = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen); // Toggle mobile sidebar visibility
  };
  const handleTabClick = (tabName, path) => {
    setSelectedTab(tabName); // Set the selected tab on click
    navigate(path);  // Navigate to the correct route
    if (isMobileSidebarOpen) {
      setIsMobileSidebarOpen(false); // Close the sidebar on mobile after selecting a tab
    }
  };

  useEffect(() => {
    setSelectedTab("Investors");  // Default selected tab
  }, []);

  const navigation = [
    {
      name: "Investors",
      path: "/admin/investors",  // Route path
      icon: investorIcon,
      icon2: investorIconBlue,
      current: selectedTab === "Investors",
    },
    {
      name: "Schemes",
      path: "/admin/schemes",
      icon: reportIcon,
      icon2: reportIconBlue,
      current: selectedTab === "Schemes",
    },
    {
      name: "User Management",
      path: "/admin/user-management",
      icon: managmentIcon,
      icon2: managmentIcon,
      current: selectedTab === "User Management",
    },
    {
      name: "Upload Data",
      path: "/admin/upload-data",
      icon: uploadIcon,
      icon2: uploadIconBlue,
      current: selectedTab === "Upload Data",
    },
    {
      name: "Email Templates",
      path: "/admin/email-templates",
      icon: emailIcon,
      icon2: emailIconBlue,
      current: selectedTab === "Email Templates",
    },
    {
      name: "Ticker",
      path: "/admin/ticker",
      icon: dashboardIcon,
      icon2: dashboardIconBlue,
      current: selectedTab === "Ticker",
    },

  ];

  const handleLogout = () => {
    dispatch(loaderSet(true))
    dispatch(nullUserDetails())
    navigate("/")
    setTimeout(() => {
      dispatch(loaderSet(false))
    }, 3000);

  }

  const handleNavigateToProfile = () => {
    dispatch(loaderSet(true))
    navigate("/admin/profile")
    setTimeout(() => {
      // dispatch(nullUserDetails())
      dispatch(loaderSet(false))
    }, 3000);

  }


  return (
    <TooltipProvider>
      <div className="flex min-h-screen w-full flex-col bg-muted/40">
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-10 flex flex-col transition-all duration-300 ease-in-out bg-background ${collapsed ? "w-14" : "w-60"
            } ${isMobileSidebarOpen ? "block" : "hidden"} sm:flex`}
        >
          {/* accessiblity fix for silktide issues - semantic navigation list */}
          <nav className="flex flex-col items-center px-2 py-5" aria-label="Primary navigation">
            <ul className="flex flex-col items-center gap-4 w-full">
              {navigation.map((item, index) => (
                <li key={index} className="w-full list-none">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.path}
                        onClick={() => handleTabClick(item.name, item.path)} // Handle tab click
                        aria-label={`${item.name} navigation item`}
                        aria-current={item.current ? "page" : undefined}
                        className={`flex items-center gap-4 px-2.5 h-9 w-full rounded-lg transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-[#0D215C] focus-visible:ring-offset-2 ${item.current ? "bg-accent text-[#0D215C]" : "text-black"
                          }`}
                      >
                        <span
                          className={`flex items-center ${collapsed
                            ? "w-full justify-center"
                            : "w-auto justify-start"
                            }`}
                        >
                          <img
                            src={item.current ? item.icon2 : item.icon}
                            alt=""
                            aria-hidden="true"
                            className={`w-5 font-bold`}
                          />
                        </span>
                        {!collapsed && <span className=" ml-2">{item.name}</span>}
                      </Link>
                    </TooltipTrigger>
                    {collapsed && (
                      <TooltipContent side="right">{item.name}</TooltipContent>
                    )}
                  </Tooltip>
                </li>
              ))}
            </ul>
          </nav>
          <button
            type="button"
            className="mt-auto mb-4 p-5 text-black w-full"
            onClick={handleToggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <PanelLeft className="h-5 w-5" />
          </button>
        </aside>

        {/* Main Content */}
        <div
          className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${collapsed ? "ml-14" : "ml-60"
            }`}
        >
          <header className="sticky w-full justify-between items-center top-0 z-30 flex h-16 gap-4 border-b bg-background px-4 ">
            <Breadcrumb className="hidden md:flex pl-5">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/admin">Admin</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>{location.pathname === "/admin/profile" ? "Profile" : selectedTab}</BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            {/* Dropdown for mobile and avatar for desktop */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Open user menu"
                    className="overflow-hidden rounded-full items-center hidden sm:flex w-11 h-11"
                  >
                    <img
                      src={userInfo && userInfo.image_url ? userInfo.image_url : "./avatar.png"}
                      width={38}
                      height={38}
                      alt="Avatar"
                      className="overflow-hidden rounded-full"
                    />
                  </Button>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem><button onClick={() => { handleNavigateToProfile() }} >Profile</button></DropdownMenuItem>
                <DropdownMenuItem><button onClick={() => { handleLogout() }} >Logout</button></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* HiOutlineBars3 button for mobile */}
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Open navigation menu"
              className="sm:hidden"
              onClick={handleMobileSidebarToggle}
            >
              <HiOutlineBars3 size={24} />
            </Button>
          </header>

          <main className="flex-1 h-screen overflow-y-auto p-4">
            {/* accessiblity fix for silktide issues - H1 heading */}
            <h1 className="sr-only">{location.pathname === "/admin/profile" ? "Profile" : selectedTab}</h1>
            {/* Check if the current route is /admin/investor-upload-report */}
            {
              location.pathname === "/admin/profile"
                ? <Profile />
                :
                location.pathname === "/admin/investor-upload-report" ? (
                  <InvestorUploadReports forWhichUserType={"admin"} />
                ) : (
                  <>
                    {/* Render content based on the selected tab */}
                    {selectedTab === "Investors" && <Investors />}
                    {selectedTab === "Schemes" && <Schemes />}
                    {selectedTab === "User Management" && <UserManagement />}
                    {selectedTab === "Upload Data" && <UploadData />}
                    {selectedTab === "Email Templates" && <EmailTemplate />}
                    {selectedTab === "Ticker" && <Ticker />}
                    {/* <Outlet/> */}
                  </>
                )}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
